<template>
  <ContentCard :loading="loading">
    <template #header>
      <ReviewsAgencyTabs />
    </template>

    <template #subheader>
      <ContentCardSubheader :back-to="backTo">
        {{ t('Reviews.agency.edit.partnersTitle') }}
        <template #actions>
          <button class="btn-secondary-color-s mr-2" data-testid="agency-partners-cancel-button" @click="onCancel">
            {{ t('Reviews.edit.action.cancel') }}
          </button>
          <button class="btn-primary-color-s" data-testid="agency-partners-save-button" :disabled="!toolPartnersUpdated" @click="onSubmit">
            {{ t('Reviews.edit.action.save') }}
          </button>
        </template>
      </ContentCardSubheader>
    </template>
    <div class="py-6">
      <LabelValue :label="t('Reviews.agency.edit.partners.title')" show-icon>
        <template #hint>
          <span v-html="t('Reviews.agency.edit.partners.description')" />
        </template>
        <ReviewsAgencyPartnerSearch
          :disabled="productCards.length >= MAX_TOOL_PARTNERS"
          :loading="productsLoading"
          :products="matchingProducts"
          @search="handleSearch"
          @selected="productSelected"
        />
        <div v-if="selectedPartnerIds.length > 1" class="flex items-start pt-8 text-body-s text-subtle">
          <MdiSvg class="mt-1 mr-1" :path="mdiInformationOutline" :size="14" />
          <span v-html="t('Reviews.agency.edit.partners.cards.hint').toString()"></span>
        </div>
        <VueDraggable
          v-model="productCards"
          :animation="150"
          ghost-class="opacity-50"
          class="grid grid-cols-1 gap-x-5 gap-y-12 pt-8 tablet:grid-cols-3 desktop:grid-cols-4"
          @update="toolPartnersUpdated = true"
        >
          <div v-for="(product, index) in productCards" :key="product.id" class="rounded-md border border-subtle px-5 pt-2 pb-4">
            <div class="mt-2 mb-4 flex items-center justify-between">
              <div class="-ml-1 flex cursor-move flex-col text-disabled">
                <MdiSvg class="-my-2.5" :path="mdiDotsHorizontal" size="30" />
                <MdiSvg class="-my-2.5" :path="mdiDotsHorizontal" size="30" />
              </div>
              <div class="flex items-center gap-x-2">
                <button
                  class="btn-link-color-m flex items-center"
                  data-testid="agency-partner-delete-button"
                  @click="handleDeletePartner(product.id)"
                >
                  <MdiSvg :path="mdiTrashCanOutline" size="20" />
                </button>
                <button
                  class="btn-link-color-m disabled:text-icon-disabled hover:disabled:text-icon-disabled"
                  data-testid="agency-partner-move-left"
                  :disabled="index < 1"
                  @click="movePartner(index, MoveDirection.LEFT)"
                >
                  <MdiSvg :path="mdiArrowLeft" size="20" />
                </button>
                <button
                  class="btn-link-color-m disabled:text-icon-disabled hover:disabled:text-icon-disabled"
                  data-testid="agency-partner-move-right"
                  :disabled="index > productCards.length - 2"
                  @click="movePartner(index, MoveDirection.RIGHT)"
                >
                  <MdiSvg :path="mdiArrowRight" size="20" />
                </button>
              </div>
            </div>
            <ProductCard class="!cursor-move" data-testid="product-card" :product="product" />
          </div>
        </VueDraggable>
      </LabelValue>
    </div>
  </ContentCard>
  <EditCloseConfirmModal ref="confirmCloseModal" :back-to="backTo" />
  <ConfirmModal
    v-if="showConfirmDeleteModal"
    :approve="t('Reviews.edit.confirmModal.delete.confirmBtn.label').toString()"
    :cancel="t('Reviews.edit.confirmModal.delete.cancelBtn.label').toString()"
    :title="t('Reviews.edit.confirmModal.delete.title').toString()"
    :description="t('Reviews.edit.confirmModal.delete.description').toString()"
    wrapper-class="max-w-xl"
    @close-dialog="onConfirmDelete"
  />
</template>

<script setup lang="ts">
import { mdiArrowLeft, mdiArrowRight, mdiDotsHorizontal, mdiInformationOutline, mdiTrashCanOutline } from '@mdi/js'
import { MdiSvg } from '@ramp106/omrjs-core-ui'
import { ProductCard } from '@ramp106/omrjs-reviews-ui'
import { track } from '@ramp106/omrjs-tracking-reviews'
import { computed, inject, ref, useTemplateRef } from 'vue'
import { VueDraggable } from 'vue-draggable-plus'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { ConfirmModal } from '@/components'
import { ReviewsAgencyTabs } from '@/components/ReviewsAgenciesManagements'
import ReviewsAgencyPartnerSearch from '@/components/ReviewsAgenciesManagements/Partners/ReviewsAgencyPartnerSearch.vue'
import EditCloseConfirmModal from '@/components/ReviewsManagement/EditCloseConfirmModal.vue'
import {
  type AgencyData,
  type ManageAgencyAttributes,
  mapProductToProductCard,
  type ProductBasicDataFragment,
  useReviewsAgencyToolPartnerOptionsLazyQuery,
  useUpdateReviewsAgencyMutation,
} from '@/gql/reviews'
import { showNotification } from '@/helpers/notificationHelper.ts'
import { ReviewsRouteName } from '@/router/types'
import { reloadReviewsAgencyKey } from '@/symbols/reviewsReloadKeys.ts'
import ContentCard from '@/ui/ContentCard.vue'
import ContentCardSubheader from '@/ui/ContentCardSubheader.vue'
import LabelValue from '@/ui/LabelValue.vue'

const props = defineProps<{
  agency: AgencyData
}>()

enum MoveDirection {
  LEFT = -1,
  RIGHT = 1,
}

const MAX_TOOL_PARTNERS = 24

const reloadReviewsAgency = inject(reloadReviewsAgencyKey) as () => void

const { t } = useI18n()
const router = useRouter()

const backTo = { name: ReviewsRouteName.ReviewsAgencyViewPartners, params: { agencyId: props.agency.id } }
const searchTerm = ref('')
const showConfirmDeleteModal = ref(false)
const confirmCloseModal = useTemplateRef('confirmCloseModal')
const selectedPartnerIds = computed<string[]>(() => productCards.value.map((product) => product.id))

const productCards = ref(props.agency.toolPartners.map((partner) => mapProductToProductCard(partner.product)))

const toolPartnersUpdated = ref(false)

function onCancel() {
  track('profile_update_cancel', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  if (toolPartnersUpdated.value) {
    confirmCloseModal.value?.show()
  } else {
    router.push(backTo)
  }
}

const variables = computed(() => ({
  searchTerm: searchTerm.value,
  exclude: selectedPartnerIds.value,
}))

const {
  load: loadProducts,
  loading: productsLoading,
  result: agencyPartnersOptionsQuery,
} = useReviewsAgencyToolPartnerOptionsLazyQuery(variables, { clientId: 'reviews' })

const matchingProducts = computed<ProductBasicDataFragment[]>(() => {
  if (searchTerm.value.length < 3 || productsLoading.value || !agencyPartnersOptionsQuery.value?.exploreProducts?.products) {
    return []
  }
  return agencyPartnersOptionsQuery.value?.exploreProducts?.products
})

async function handleSearch(search: string) {
  if (search.length >= 3) {
    searchTerm.value = search
    await loadProducts()
  }
}

function productSelected(product: ProductBasicDataFragment) {
  toolPartnersUpdated.value = true
  productCards.value.push(mapProductToProductCard(product))
}

const partnerToRemove = ref<string | null>(null)

function handleDeletePartner(productId: string) {
  partnerToRemove.value = productId
  showConfirmDeleteModal.value = true
}

function onConfirmDelete(confirm: boolean) {
  if (confirm) {
    productCards.value = productCards.value.filter((product) => product.id !== partnerToRemove.value)
    toolPartnersUpdated.value = true
  }
  partnerToRemove.value = null
  showConfirmDeleteModal.value = false
}

function movePartner(index: number, direction: MoveDirection) {
  const newIndex = index + direction
  if (newIndex < 0 || newIndex >= productCards.value.length) {
    return
  }
  toolPartnersUpdated.value = true
  ;[productCards.value[index], productCards.value[newIndex]] = [productCards.value[newIndex], productCards.value[index]]
}

const { mutate, loading, onDone, onError } = useUpdateReviewsAgencyMutation({ clientId: 'reviews' })

function prepareAgencyProductsAttributes() {
  const agencyProductsAttributes: ManageAgencyAttributes['agencyProductsAttributes'] = []

  productCards.value.forEach((product, index) => {
    const existingPartner = props.agency.toolPartners.find((partner) => partner.product.id === product.id)

    if (!existingPartner) {
      agencyProductsAttributes.push({
        productId: product.id,
        adpPosition: index + 1,
      })
    } else if (existingPartner.adpPosition !== index + 1) {
      agencyProductsAttributes.push({
        id: existingPartner.id,
        adpPosition: index + 1,
      })
    }
  })

  props.agency.toolPartners.forEach((partner) => {
    const stillExists = productCards.value.some((product) => product.id === partner.product.id)

    if (!stillExists) {
      agencyProductsAttributes.push({
        id: partner.id,
        _destroy: true,
      })
    }
  })

  return agencyProductsAttributes
}

async function onSubmit() {
  track('profile_update', { surface: 'editor', entity_type: 'service', entity_id: props.agency.id })
  const agencyProductsAttributes = prepareAgencyProductsAttributes()
  await mutate({
    id: props.agency.id,
    attributes: { agencyProductsAttributes },
  })
}

onDone(() => {
  reloadReviewsAgency()
  showNotification(t('Reviews.agency.notifications.savedSuccessfully', { name: props.agency.title }), 'success')
  router.push(backTo)
})

onError((error) => {
  if (error) {
    showNotification(error.message, 'error')
  }
})
</script>
